import AlwaysRequireResponse from '../classes/AlwaysRequireResponse';
import CountdownResponse from '../classes/CountdownResponse';
import GenerateCodeResponse from '../classes/GenerateCodeResponse';
import UserInfo from '../classes/UserInfo';
import UserInfoResponse from '../classes/UserInfoResponse';
import VerifyCodeData from '../classes/VerifyCodeData';
import VerifyResponse from '../classes/VerifyResponse';
import { errorMessages } from '../constants/error-messages';
import utils from '../util/utils';

const MFA_API_DOMAIN = utils.getEnvProperty('MFA_API_DOMAIN');
const MFA_API_PATH = utils.getEnvProperty('MFA_API_PATH');

declare global {
    interface Window {
        // eslint-disable-next-line
        Liferay: any;
    }
}

interface FetchMFAOptions {
    endpoint: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: object;
    fallbackErrorMessage?: string;
}

interface FetchMFAResult<T> {
    data: T | null;
    errorMessage: string | null;
}

/**
 * Wrapper around the fetch API to make authenticated requests to the MFA API.
 * Handles authentication, error handling, and returns the response data.
 *
 * @param endpoint - The API endpoint path (e.g. '/masked-contact-info')
 * @param method - HTTP method (defaults to 'GET')
 * @param body - Optional request body (will be JSON serialized)
 * @param fallbackErrorMessage - Fallback error message if the API does not return one
 * @returns FetchMFAResult containing the parsed response data or an error message
 */
const fetchMFA = async <T>({
    endpoint,
    method = 'GET',
    body,
    fallbackErrorMessage = errorMessages.DEFAULT_ERROR,
}: FetchMFAOptions): Promise<FetchMFAResult<T>> => {
    const url = MFA_API_DOMAIN + MFA_API_PATH + endpoint;

    const headers: HeadersInit = {
        Authorization: 'Bearer ' + window.sessionStorage.mfaToken,
        ...(body ? { 'Content-Type': 'application/json' } : {}),
    };

    const result: FetchMFAResult<T> = { data: null, errorMessage: null };

    await fetch(url, {
        method,
        headers,
        ...(body ? { body: JSON.stringify(body) } : {}),
    })
        .then(response => {
            if (!response.ok) {
                return response.json().then(errData => {
                    result.errorMessage = errData?.message || fallbackErrorMessage;
                    throw new Error();
                }).catch(() => {
                    throw new Error();
                });
            }
            return response.json();
        })
        .then((data: T) => {
            result.data = data;
        })
        .catch(() => {
            if (!result.errorMessage) {
                result.errorMessage = fallbackErrorMessage;
            }
        });

    return result;
};

/**
 * Get the user info for mfa
 *
 * @returns Json: object
 */
export const getUserInfo = async (): Promise<UserInfoResponse> => {
    const returnObject = new UserInfoResponse({} as UserInfo, []);

    const { data, errorMessage } = await fetchMFA<UserInfo>({
        endpoint: '/masked-contact-info',
        fallbackErrorMessage: errorMessages.CONTACT_ERROR,
    });

    if (errorMessage) {
        returnObject.errorMessages.push(errorMessage);
    } else if (data) {
        Object.assign(returnObject.userInfo, data);
    }

    return returnObject;
};


export const generateToken = async (isMobile: boolean): Promise<GenerateCodeResponse> => {
    const returnObject = new GenerateCodeResponse(false, []);

    const { data, errorMessage } = await fetchMFA<boolean>({
        endpoint: `/mfa-code?contact_type=${isMobile ? 'mobile' : 'email'}`,
        fallbackErrorMessage: errorMessages.GENERATE_ERROR,
    });

    if (errorMessage) {
        returnObject.errorMessages.push(errorMessage);
    } else {
        returnObject.success = true;
    }

    return returnObject;
}

export const validateToken = async (data: VerifyCodeData): Promise<VerifyResponse> => {
    const returnObject = new VerifyResponse(false, []);

    const { data: responseData, errorMessage } = await fetchMFA<boolean>({
        endpoint: '/mfa-code/validate',
        method: 'POST',
        body: data,
        fallbackErrorMessage: errorMessages.VALIDATE_ERROR,
    });

    if (errorMessage) {
        returnObject.errorMessages.push(errorMessage);
    } else if (responseData !== null) {
        returnObject.success = responseData;
    }

    return returnObject;
}

export const isAlwaysRequired = async (): Promise<AlwaysRequireResponse> => {
    const returnObject = new AlwaysRequireResponse(false, []);

    const { data, errorMessage } = await fetchMFA<boolean>({
        endpoint: '/always-required',
        fallbackErrorMessage: errorMessages.DEFAULT_ERROR,
    });

    if (errorMessage) {
        returnObject.errorMessages.push(errorMessage);
    } else if (data !== null) {
        returnObject.isAlwaysRequired = data;
    }

    return returnObject;
}

export const countdownTime = async (): Promise<CountdownResponse> => {
    const returnObject = new CountdownResponse(0, []);

    const { data, errorMessage } = await fetchMFA<number>({
        endpoint: '/countdown',
        fallbackErrorMessage: errorMessages.DEFAULT_ERROR,
    });

    if (errorMessage) {
        returnObject.errorMessages.push(errorMessage);
    } else if (data !== null) {
        returnObject.minutes = data;
    }

    return returnObject;
}
